import { ScrollStrategy, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { TextFieldModule } from '@angular/cdk/text-field';
import { DatePipe, NgClass } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    DOCUMENT,
    DestroyRef,
    ElementRef,
    HostBinding,
    Inject,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewEncapsulation,
    inject,
    signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marked } from 'marked';
import { Subject } from 'rxjs';
import { AI_CONSTANTS } from './ai-chat.constants';
import { AiChatService } from './ai-chat.service';
import { AiAssistantStreamEvent, AiChatMessage, AiChatProposal, AiConversationListItem } from './ai-chat.types';

const MODEL_STORAGE_KEY = 'ai-chat.model';

@Component({
    selector: 'ai-chat',
    templateUrl: './ai-chat.component.html',
    styleUrls: ['./ai-chat.component.scss'],
    encapsulation: ViewEncapsulation.None,
    exportAs: 'aiChat',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        NgClass,
        DatePipe,
        MatIconModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        TextFieldModule,
        TranslocoDirective,
        MatTooltip,
        MatButtonToggleModule,
    ],
})
export class AiChatComponent implements OnInit, AfterViewInit, OnDestroy {
    readonly messageInput = viewChild<ElementRef>('messageInput');
    destroyRef = inject(DestroyRef);
    readonly messages = signal<AiChatMessage[]>([]);
    readonly conversations = signal<AiConversationListItem[]>([]);
    readonly busy = signal(false);
    readonly showHistory = signal(false);
    /** Model choices of the configuration; the toggle shows only with two or more. */
    readonly models = signal<string[]>([]);
    /** Model of the current conversation: chosen before the first message, then fixed. */
    readonly modelKey = signal<string | null>(null);
    private _defaultModel: string | null = null;
    conversationId: number | null = null;
    opened: boolean = false;
    private _abort: AbortController | null = null;
    private _mutationObserver: MutationObserver;
    private _scrollStrategy: ScrollStrategy = this._scrollStrategyOptions.block();
    private _overlay: HTMLElement;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        @Inject(DOCUMENT) private _document: Document,
        private _elementRef: ElementRef,
        private _renderer2: Renderer2,
        private _quickChatService: AiChatService,
        private _scrollStrategyOptions: ScrollStrategyOptions,
        private transloco: TranslocoService
    ) {
        this.initChat();
        this._loadModels();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Decorated methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Host binding for component classes
     */
    @HostBinding('class') get classList(): any {
        return {
            'ai-chat-opened': this.opened,
        };
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {}

    /**
     * After view init
     */
    ngAfterViewInit(): void {
        // Fix for Firefox.
        //
        // Because 'position: sticky' doesn't work correctly inside a 'position: fixed' parent,
        // adding the '.cdk-global-scrollblock' to the html element breaks the navigation's position.
        // This fixes the problem by reading the 'top' value from the html element and adding it as a
        // 'marginTop' to the navigation itself.
        this._mutationObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                const mutationTarget = mutation.target as HTMLElement;
                if (mutation.attributeName === 'class') {
                    if (mutationTarget.classList.contains('cdk-global-scrollblock')) {
                        const top = parseInt(mutationTarget.style.top, 10);
                        this._renderer2.setStyle(this._elementRef.nativeElement, 'margin-top', `${Math.abs(top)}px`);
                    } else {
                        this._renderer2.setStyle(this._elementRef.nativeElement, 'margin-top', null);
                    }
                }
            });
        });
        this._mutationObserver.observe(this._document.documentElement, {
            attributes: true,
            attributeFilter: ['class'],
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Disconnect the mutation observer
        this._mutationObserver.disconnect();

        this._abort?.abort();

        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the panel
     */
    open(): void {
        // Return if the panel has already opened
        if (this.opened) {
            return;
        }

        // Open the panel
        this._toggleOpened(true);
    }

    /**
     * Close the panel
     */
    close(): void {
        // Return if the panel has already closed
        if (!this.opened) {
            return;
        }

        // Close the panel
        this._toggleOpened(false);
    }

    /**
     * Toggle the panel
     */
    toggle(): void {
        if (this.opened) {
            this.close();
        } else {
            this.open();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Show the backdrop
     *
     * @private
     */
    private _showOverlay(): void {
        // Try hiding the overlay in case there is one already opened
        this._hideOverlay();

        // Create the backdrop element
        this._overlay = this._renderer2.createElement('div');

        // Return if overlay couldn't be created for some reason
        if (!this._overlay) {
            return;
        }

        // Add a class to the backdrop element
        this._overlay.classList.add('ai-chat-overlay');

        // Append the backdrop to the parent of the panel
        this._renderer2.appendChild(this._elementRef.nativeElement.parentElement, this._overlay);

        // Enable block scroll strategy
        this._scrollStrategy.enable();

        // Add an event listener to the overlay
        this._overlay.addEventListener('click', () => {
            this.close();
        });
    }

    /**
     * Hide the backdrop
     *
     * @private
     */
    private _hideOverlay(): void {
        if (!this._overlay) {
            return;
        }

        // If the backdrop still exists...
        if (this._overlay) {
            // Remove the backdrop
            this._overlay.parentNode.removeChild(this._overlay);
            this._overlay = null;
        }

        // Disable block scroll strategy
        this._scrollStrategy.disable();
    }

    /**
     * Open/close the panel
     *
     * @param open
     * @private
     */
    private _toggleOpened(open: boolean): void {
        // Set the opened
        this.opened = open;

        // If the panel opens, show the overlay
        if (open) {
            this._showOverlay();
        }
        // Otherwise, hide the overlay
        else {
            this._hideOverlay();
        }
    }

    /**
     * Sends the prompt and streams the answer into a new assistant message.
     */
    chatWithAi($event: Event, inputElem: HTMLTextAreaElement): void {
        $event.preventDefault();

        const prompt = inputElem?.value?.trim();
        if (!prompt || this.busy()) {
            return;
        }

        inputElem.value = '';

        // Writing instead of deciding rejects the open proposals (the server does the same).
        this._pendingProposals().forEach((p) => (p.state = 'rejected'));
        this._send({ conversationId: this.conversationId, prompt }, prompt);
    }

    /**
     * Approves or rejects a proposed write; the assistant then runs it (or not) and answers.
     */
    decide(proposal: AiChatProposal, approved: boolean): void {
        if (this.busy() || proposal.state !== 'pending' || (approved && !proposal.canApprove)) {
            return;
        }

        proposal.state = approved ? 'approved' : 'rejected';
        this._refresh();

        // The server rejects the proposals of a turn left unanswered: the decisions on a turn's
        // proposals go together, once every one of them has its own.
        const proposals = this.messages().find((m) => m.proposals?.includes(proposal))?.proposals ?? [proposal];
        if (proposals.some((p) => p.state === 'pending')) {
            return;
        }

        this._send(
            {
                conversationId: this.conversationId,
                approvals: proposals.map((p) => ({ requestId: p.requestId, approved: p.state === 'approved' })),
            },
            null
        );
    }

    /** True for a decided proposal whose turn still has proposals to decide (nothing is sent yet). */
    awaitsOthers(message: AiChatMessage, proposal: AiChatProposal): boolean {
        return proposal.state !== 'pending' && (message.proposals ?? []).some((p) => p.state === 'pending');
    }

    /** Picks the model of a new conversation and remembers it for the next ones (this browser only). */
    selectModel(key: string): void {
        if (this.conversationId !== null) {
            return;
        }
        this.modelKey.set(key);
        try {
            localStorage.setItem(MODEL_STORAGE_KEY, key);
        } catch {
            // storage unavailable: the choice lasts for this conversation only
        }
    }

    modelLabel(key: string): string {
        const translationKey = `components.ai-chat.models.${key}`;
        const label = this.transloco.translate(translationKey);
        return label === translationKey ? key : label;
    }

    private _send(
        payload: {
            conversationId: number | null;
            prompt?: string;
            approvals?: { requestId: string; approved: boolean }[];
            modelKey?: string;
        },
        userText: string | null
    ): void {
        this.showHistory.set(false);
        if (payload.conversationId === null && this.modelKey()) {
            payload = { ...payload, modelKey: this.modelKey() };
        }

        const answer: AiChatMessage = { role: 'assistant', text: '', html: '', tools: [], streaming: true };
        this.messages.update((messages) => [
            ...messages,
            ...(userText ? [{ role: 'user' as const, text: userText, tools: [] }] : []),
            answer,
        ]);
        this.busy.set(true);

        this._abort = new AbortController();
        this._quickChatService
            .streamChat(payload, (event) => this._onStreamEvent(answer, event), this._abort.signal)
            .catch((err) => {
                if (err?.name !== 'AbortError') {
                    this._failAnswer(answer, String(err?.message ?? err));
                }
            })
            .finally(() => {
                answer.streaming = false;
                this.busy.set(false);
                this._refresh();
            });
    }

    /**
     * Starts a new conversation (the previous one stays in the history).
     */
    initChat(): void {
        this._abort?.abort();
        this.conversationId = null;
        this.showHistory.set(false);
        this.modelKey.set(this._preferredModel());
        this.messages.set([
            {
                role: 'assistant',
                text: '',
                html: this.transloco.translate('components.ai-chat.ai-welcome-message', {
                    botName: AI_CONSTANTS.BOT_NAME,
                }),
                tools: [],
            },
        ]);
    }

    /**
     * Shows the user's past conversations.
     */
    openHistory(): void {
        this.showHistory.set(true);
        this._quickChatService
            .getConversations()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((res) => this.conversations.set(res.result ?? []));
    }

    /**
     * Reopens a past conversation to continue it.
     */
    loadConversation(id: number): void {
        this._quickChatService
            .getConversation(id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((res) => {
                this.conversationId = res.result.id;
                this.modelKey.set(res.result.modelKey ?? this._defaultModel);
                this.messages.set(
                    res.result.messages.map((m) => ({
                        role: m.role,
                        text: m.text,
                        html: m.role === 'assistant' ? this._renderMarkdown(m.text) : undefined,
                        tools: m.toolNames ?? [],
                    }))
                );
                const last = this.messages().at(-1);
                if (last?.role === 'assistant' && res.result.pendingProposals?.length) {
                    last.proposals = res.result.pendingProposals.map((p) => ({ ...p, state: 'pending' }));
                }
                this.showHistory.set(false);
            });
    }

    deleteConversation(id: number, $event: Event): void {
        $event.stopPropagation();
        this._quickChatService
            .deleteConversation(id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.conversations.update((list) => list.filter((c) => c.id !== id));
                if (this.conversationId === id) {
                    this.initChat();
                    this.showHistory.set(true);
                }
            });
    }

    toolLabel(tool: string): string {
        const key = `components.ai-chat.tools.${tool}`;
        const label = this.transloco.translate(key);
        return label === key ? tool : label;
    }

    private _onStreamEvent(answer: AiChatMessage, event: AiAssistantStreamEvent): void {
        switch (event.type) {
            case 'conversation':
                this.conversationId = event.conversationId;
                break;
            case 'text':
                answer.text += event.text;
                answer.html = this._renderMarkdown(answer.text);
                break;
            case 'tool':
                if (!answer.tools.includes(event.toolName)) {
                    answer.tools = [...answer.tools, event.toolName];
                }
                break;
            case 'approval':
                answer.proposals = [...(answer.proposals ?? []), { ...event.proposal, state: 'pending' }];
                break;
            case 'error':
                this._failAnswer(answer, event.text);
                break;
        }
        this._refresh();
    }

    private _loadModels(): void {
        this._quickChatService
            .getModels()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((res) => {
                this.models.set(res.result?.models ?? []);
                this._defaultModel = res.result?.defaultModel ?? null;
                if (this.conversationId === null) {
                    this.modelKey.set(this._preferredModel());
                }
            });
    }

    /** The choice remembered in this browser, when the configuration still offers it; else the default. */
    private _preferredModel(): string | null {
        let stored: string | null = null;
        try {
            stored = localStorage.getItem(MODEL_STORAGE_KEY);
        } catch {
            // storage unavailable
        }
        return stored && this.models().includes(stored) ? stored : this._defaultModel;
    }

    private _pendingProposals(): AiChatProposal[] {
        return this.messages()
            .flatMap((m) => m.proposals ?? [])
            .filter((p) => p.state === 'pending');
    }

    private _failAnswer(answer: AiChatMessage, error: string): void {
        answer.error = true;
        const message = this.transloco.translate('ai-chat.assistant-error', { error });
        answer.text = answer.text ? `${answer.text}\n\n${message}` : message;
        answer.html = this._renderMarkdown(answer.text);
    }

    private _renderMarkdown(text: string): string {
        // Angular sanitizes the [innerHTML] binding: no script or event handler survives.
        return marked.parse(text ?? '', { async: false, gfm: true, breaks: true });
    }

    /** Messages are mutated in place while streaming: a new array reference re-renders them. */
    private _refresh(): void {
        this.messages.update((messages) => [...messages]);
    }

    protected readonly AI_CONSTANTS = AI_CONSTANTS;
}
